import React from "react";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
} from "@mui/material";
import MultiTextInput from "./MultiTextInput";

type Props = {
  enabled: boolean;

  maxSessionsEnabled: boolean;
  maxSessionsPerUser: number;
  onToggleEnabled: (enabled: boolean) => void;
  onToggleMaxSessions: (enabled: boolean) => void;
  onMaxSessionsChange: (value: number) => void;

  // Email allowlist controls
  emailsEnabled: boolean;
  emails: string[];
  onToggleEmailsEnabled: (enabled: boolean) => void;
  onEmailsChange: (values: string[]) => void;

  // Domain allowlist controls
  domainsEnabled: boolean;
  domains: string[];
  onToggleDomainsEnabled: (enabled: boolean) => void;
  onDomainsChange: (values: string[]) => void;

  // Optional inline errors
  errors?: Partial<{
    maxSessionsPerUser: string;
    emails: string;
    domains: string;
  }>;
};

const UserAccessControlEditor: React.FC<Props> = ({
  enabled,

  maxSessionsEnabled,
  maxSessionsPerUser,
  onToggleEnabled,
  onToggleMaxSessions,
  onMaxSessionsChange,

  emailsEnabled,
  emails,
  onToggleEmailsEnabled,
  onEmailsChange,

  domainsEnabled,
  domains,
  onToggleDomainsEnabled,
  onDomainsChange,

  errors,
}) => {
  // When toggled OFF, clear values and collapse inputs
  const handleToggleEmails = (checked: boolean) => {
    onToggleEmailsEnabled(checked);
    if (!checked && emails.length) onEmailsChange([]);
  };

  const handleToggleDomains = (checked: boolean) => {
    onToggleDomainsEnabled(checked);
    if (!checked && domains.length) onDomainsChange([]);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        User Access Control (Optional)
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e) => onToggleEnabled(e.target.checked)}
          />
        }
        label="Configure user access control"
      />

      {enabled && (
        <Box sx={{ pl: 2, mt: 2, borderLeft: "3px solid #ccc" }}>
          {/* Max sessions per user */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="body1">Max Sessions per User</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={maxSessionsEnabled}
                  onChange={(e) => onToggleMaxSessions(e.target.checked)}
                />
              }
              label={maxSessionsEnabled ? "Enabled" : "Disabled"}
              labelPlacement="start"
            />
          </Box>

          <TextField
            label="Max number of sessions per user"
            type="number"
            fullWidth
            disabled={!maxSessionsEnabled}
            value={maxSessionsPerUser}
            onChange={(e) =>
              onMaxSessionsChange(parseInt(e.target.value, 10) || 1)
            }
            sx={{ mt: 1 }}
            helperText={
              errors?.maxSessionsPerUser ||
              "Maximum number of sessions a user can initiate with this link"
            }
            error={Boolean(errors?.maxSessionsPerUser)}
          />

          {/* Email allowlist */}
          <Box mt={3} />
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="body1">Email Allowlist</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={emailsEnabled}
                  onChange={(e) => handleToggleEmails(e.target.checked)}
                />
              }
              label={emailsEnabled ? "Enabled" : "Disabled"}
              labelPlacement="start"
            />
          </Box>
          {emailsEnabled && (
            <MultiTextInput
              label="Allowed emails"
              placeholder="eli@thegoldsteins.com"
              value={emails}
              onChange={onEmailsChange}
              errorText={errors?.emails}
            />
          )}

          {/* Domain allowlist */}
          <Box mt={3} />
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="body1">Email Domains Allowlist</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={domainsEnabled}
                  onChange={(e) => handleToggleDomains(e.target.checked)}
                />
              }
              label={domainsEnabled ? "Enabled" : "Disabled"}
              labelPlacement="start"
            />
          </Box>
          {domainsEnabled && (
            <MultiTextInput
              label="Allowed domains"
              placeholder="persona-ai.ai"
              value={domains}
              onChange={onDomainsChange}
              errorText={errors?.domains}
              // Normalize to bare domain (strip leading "@")
              normalize={(s: string) => (s.startsWith("@") ? s.slice(1) : s)}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default UserAccessControlEditor;
